import type { CSSProperties } from "react";

export type Tone = {
  accent: string;
  tint: string;
};

export const CANONICAL_TONES: readonly Tone[] = [
  { accent: "#6f7cff", tint: "rgba(111, 124, 255, 0.13)" },
  { accent: "#10b981", tint: "rgba(16, 185, 129, 0.13)" },
  { accent: "#ef6461", tint: "rgba(239, 100, 97, 0.13)" },
  { accent: "#f59e0b", tint: "rgba(245, 158, 11, 0.13)" },
  { accent: "#c76dff", tint: "rgba(199, 109, 255, 0.13)" },
  { accent: "#22a7c7", tint: "rgba(34, 167, 199, 0.13)" },
  { accent: "#4c9aff", tint: "rgba(76, 154, 255, 0.13)" },
  { accent: "#c99135", tint: "rgba(201, 145, 53, 0.13)" },
  { accent: "#8f6bff", tint: "rgba(143, 107, 255, 0.13)" },
  { accent: "#e56b8c", tint: "rgba(229, 107, 140, 0.13)" },
] as const;

export const NEUTRAL_TONE: Tone = {
  accent: "var(--color-text-primary)",
  tint: "transparent",
};

export const toneByIndex = (index: number): Tone => (
  CANONICAL_TONES[Math.abs(index) % CANONICAL_TONES.length]
);

const NOTIFICATION_TONE_INDEX: Record<string, number> = {
  act: 0,
  // canonical confirm-loop words first; the retired confirmation/warning
  // spellings read-tolerate for persisted pre-wave rows.
  confirm: 1,
  confirmation: 1,
  critical: 2,
  warn: 3,
  warning: 3,
  report: 4,
};

const INTEGRATION_TONE_INDEX: Record<string, number> = {
  entity: 1,
  tool: 5,
  channel: 6,
  file: 7,
};

const ARTIFACT_TONE_INDEX: Record<string, number> = {
  workflow: 0,
  message: 3,
  report: 4,
  entity: 1,
  table: 1,
  integration: 8,
  chart: 8,
  tool: 5,
  channel: 6,
  icon: 6,
  file: 7,
};

const keyOf = (value: unknown): string => String(value || "").toLowerCase().trim();

export const toneForNotification = (level: unknown, fallbackIndex = 0): Tone => {
  const key = keyOf(level);
  if (key === "all") return NEUTRAL_TONE;
  const index = NOTIFICATION_TONE_INDEX[key];
  return typeof index === "number" ? toneByIndex(index) : toneByIndex(fallbackIndex);
};

export const toneForIntegration = (type: unknown, fallbackIndex = 0): Tone => {
  const key = keyOf(type);
  if (key === "all") return NEUTRAL_TONE;
  const index = INTEGRATION_TONE_INDEX[key];
  return typeof index === "number" ? toneByIndex(index) : toneByIndex(fallbackIndex);
};

export const toneForArtifactKind = (kind: unknown, fallbackIndex = 0): Tone => {
  const key = keyOf(kind);
  const index = ARTIFACT_TONE_INDEX[key];
  return typeof index === "number" ? toneByIndex(index) : toneByIndex(fallbackIndex);
};

export const toneForParticipant = (kind: "world" | "current" | "recipient", fallbackIndex = 0): Tone => {
  if (kind === "world") return toneByIndex(0);
  if (kind === "current") return toneByIndex(1);
  return toneByIndex(fallbackIndex + 2);
};

export const artifactKindOf = (artifact: unknown, fallback: unknown = "icon"): string => {
  const value = artifact as { type?: unknown; machine?: { type?: unknown } } | undefined;
  return keyOf(value?.type || value?.machine?.type || fallback || "icon");
};

export const toneVars = (tone: Tone, prefix: "conversation-card" | "conversation-filter"): CSSProperties & Record<string, string> => ({
  [`--${prefix}-accent`]: tone.accent,
  [`--${prefix}-tint`]: tone.tint,
});
